<template>
    <div v-if="show" class="fixed inset-0 z-[60] flex items-center justify-center">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black opacity-50 modal-wrapper" @click="attemptClose"></div>

        <!-- Modal -->
        <div class="relative bg-white rounded-lg shadow-lg w-full max-w-md p-6 z-[60] m-4">
            <!-- Header -->
            <div class="flex justify-between items-center mb-6">
                <div>
                    <h2 class="text-xl font-bold text-gray-900">{{ shoppingItem?.id ? 'Edit' : 'Add' }} Shopping Item</h2>
                    <p v-if="shoppingItem?.id" class="text-gray-600 text-sm mt-1">{{ shoppingItem?.name }}</p>
                </div>
                <button @click="attemptClose" class="text-gray-600 hover:text-gray-800 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <!-- Form -->
            <form @submit.prevent="saveChanges" class="space-y-4" novalidate>
                <FormErrorSummary :errors="errorSummary" />

                <!-- Name -->
                <div v-if="isNewItem">
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Name <span class="text-red-500 ml-0.5" aria-label="required">*</span>
                    </label>
                    <input v-model="formData.name" type="text" placeholder="Item name"
                        class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors text-[16px] sm:text-sm"
                        :class="nameDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                        @blur="touch('name')" />
                    <p v-if="nameDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ nameDisplayError }}</p>
                </div>

                <!-- Quantity -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Quantity <span class="text-red-500 ml-0.5" aria-label="required">*</span>
                    </label>
                    <input v-model.number="formData.quantity" type="number" min="0" step="0.1"
                        class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors text-[16px] sm:text-sm"
                        :class="quantityDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                        @blur="touch('quantity')" />
                    <p v-if="quantityDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ quantityDisplayError }}</p>
                </div>

                <!-- Unit -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Unit <span class="text-red-500 ml-0.5" aria-label="required">*</span>
                    </label>
                    <input v-model="formData.unit" type="text" placeholder="kg, pcs, liters, etc."
                        class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors text-[16px] sm:text-sm"
                        :class="unitDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                        @blur="touch('unit')" />
                    <p v-if="unitDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ unitDisplayError }}</p>
                </div>

                <!-- Total Price -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Total Price (€) <span class="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <div class="relative">
                        <input v-model.number="formData.price" type="number" min="0" step="0.01"
                            class="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors text-[16px] sm:text-sm" />
                        <span class="absolute right-2 top-2 text-sm text-gray-500">€</span>
                    </div>
                    <p class="text-xs text-gray-500 mt-1">Enter the total price for this item (not per unit)</p>
                </div>

                <!-- Type -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Type/Category <span class="text-red-500 ml-0.5" aria-label="required">*</span>
                    </label>
                    <input v-model="formData.type" type="text" placeholder="food, toiletries, etc."
                        class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-500 transition-colors text-[16px] sm:text-sm"
                        :class="typeDisplayError ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                        @blur="touch('type')" />
                    <p v-if="typeDisplayError" class="text-red-500 text-sm mt-1" role="alert">{{ typeDisplayError }}</p>
                </div>

                <!-- Action Buttons -->
                <div class="flex justify-end space-x-3 pt-4">
                    <button type="button" @click="attemptClose"
                        class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-gray-100">
                        Cancel
                    </button>
                    <button type="submit" :disabled="saving"
                        class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed">
                        <span v-if="saving">Saving...</span>
                        <span v-else>Save Changes</span>
                    </button>
                </div>
            </form>
        </div>
    </div>

    <!-- Unsaved Changes Dialog -->
    <UnsavedChangesDialog :show="showUnsavedChangesDialog" @keep-editing="keepEditing" @discard="discardChanges" />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { CREATE_SHOPPING_ITEM, UPDATE_SHOPPING_ITEM } from '../../graphql/mutations'
import { GET_SHOPPING_LIST } from '../../graphql/queries'
import { showError, showSuccess } from '../../utils/errorHandling'
import { useFormValidation } from '../../composables/useFormValidation'
import FormErrorSummary from '../FormErrorSummary.vue'
import { vm } from '../../utils/validationMessages'
import UnsavedChangesDialog from '../UnsavedChangesDialog.vue'

// Props
interface Props {
    show: boolean
    shoppingItem: any
}

const props = defineProps<Props>()

// Emits
const emit = defineEmits<{
    close: []
    updated: []
}>()

// State
const formData = ref({
    name: '',
    quantity: 0,
    unit: '',
    price: 0,
    type: ''
})

const errors = ref({
    name: '',
    quantity: '',
    unit: '',
    price: '',
    type: ''
})

const originalFormData = ref({
    name: '',
    quantity: 0,
    unit: '',
    price: 0,
    type: ''
})

const showUnsavedChangesDialog = ref(false)
const submitAttempted = ref(false)

const { touch, displayError, reset: resetValidation } = useFormValidation()

const nameDisplayError = computed(() => displayError('name', errors.value.name))
const quantityDisplayError = computed(() => displayError('quantity', errors.value.quantity))
const unitDisplayError = computed(() => displayError('unit', errors.value.unit))
const typeDisplayError = computed(() => displayError('type', errors.value.type))

const errorSummary = computed(() => {
  if (!submitAttempted.value) return []
  const items = []
  if (errors.value.name) items.push({ label: 'Name', message: errors.value.name })
  if (errors.value.quantity) items.push({ label: 'Quantity', message: errors.value.quantity })
  if (errors.value.unit) items.push({ label: 'Unit', message: errors.value.unit })
  if (errors.value.type) items.push({ label: 'Type/Category', message: errors.value.type })
  return items
})

// Mutations with proper cache updates
const { mutate: createShoppingItem, loading: creating } = useMutation(CREATE_SHOPPING_ITEM, {
    update: (cache, { data }) => {
        if (data?.createShoppingItem && props.shoppingItem?.list_id) {
            // Update the shopping list cache with the new item
            cache.modify({
                id: cache.identify({ __typename: 'ShoppingList', id: props.shoppingItem.list_id }),
                fields: {
                    items(existingItems = []) {
                        const newItemRef = cache.writeFragment({
                            data: data.createShoppingItem,
                            fragment: gql`
                                fragment NewShoppingItem on ShoppingItem {
                                    id
                                    name
                                    list_id
                                    list_name
                                    ingredient_id
                                    quantity
                                    unit
                                    price
                                    is_bought
                                    type
                                    created_at
                                    updated_at
                                }
                            `
                        })
                        return [...existingItems, newItemRef]
                    }
                }
            })
        }
    },
    refetchQueries: () => {
        if (props.shoppingItem?.list_id) {
            return [{ query: GET_SHOPPING_LIST, variables: { id: props.shoppingItem.list_id } }]
        }
        return []
    },
    awaitRefetchQueries: true
})

const { mutate: updateShoppingItem, loading: updating } = useMutation(UPDATE_SHOPPING_ITEM, {
    update: (cache, { data }) => {
        if (data?.updateShoppingItem) {
            // Evict the old shopping list from cache to force refetch
            if (props.shoppingItem?.list_id) {
                cache.evict({ id: cache.identify({ __typename: 'ShoppingList', id: props.shoppingItem.list_id }) })
                cache.gc()
            }
        }
    },
    refetchQueries: () => {
        if (props.shoppingItem?.list_id) {
            return [{ query: GET_SHOPPING_LIST, variables: { id: props.shoppingItem.list_id } }]
        }
        return []
    },
    awaitRefetchQueries: true
})

const saving = computed(() => creating.value || updating.value)

const isNewItem = computed(() => !props.shoppingItem?.id)

// Computed
const hasUnsavedChanges = computed(() => {
    return formData.value.name !== originalFormData.value.name ||
        formData.value.quantity !== originalFormData.value.quantity ||
        formData.value.unit !== originalFormData.value.unit ||
        formData.value.price !== originalFormData.value.price ||
        formData.value.type !== originalFormData.value.type
})

// Methods
const resetForm = () => {
    const itemData = {
        name: props.shoppingItem?.name || '',
        quantity: props.shoppingItem?.quantity || 1,
        unit: props.shoppingItem?.unit || '',
        price: props.shoppingItem?.price || 0,
        type: props.shoppingItem?.type || ''
    }
    formData.value = { ...itemData }
    originalFormData.value = { ...itemData }
    errors.value = {
        name: '',
        quantity: '',
        unit: '',
        price: '',
        type: ''
    }
    submitAttempted.value = false
    resetValidation()
}

const validateForm = () => {
    errors.value = { name: '', quantity: '', unit: '', price: '', type: '' }
    let isValid = true

    if (!formData.value.name.trim()) {
        errors.value.name = vm.name.required
        isValid = false
    }

    if (!formData.value.quantity || formData.value.quantity <= 0) {
        errors.value.quantity = vm.quantity.positive
        isValid = false
    }

    if (!formData.value.unit.trim()) {
        errors.value.unit = vm.unit.required
        isValid = false
    }

    if (!formData.value.type.trim()) {
        errors.value.type = vm.itemType.required
        isValid = false
    }

    return isValid
}

const saveChanges = async () => {
    submitAttempted.value = true
    if (!validateForm()) {
        return
    }

    try {
        if (isNewItem.value) {
            // Create new item
            await createShoppingItem({
                list_id: props.shoppingItem.list_id,
                name: formData.value.name,
                ingredient_id: props.shoppingItem.ingredient_id || '',
                quantity: formData.value.quantity,
                unit: formData.value.unit,
                price: formData.value.price,
                type: formData.value.type
            })
            showSuccess('Shopping item created successfully!')
        } else {
            // Update existing item
            await updateShoppingItem({
                id: props.shoppingItem.id,
                list_id: props.shoppingItem.list_id,
                name: formData.value.name,
                ingredient_id: props.shoppingItem.ingredient_id || '',
                quantity: formData.value.quantity,
                unit: formData.value.unit,
                price: formData.value.price,
                type: formData.value.type,
                is_bought: props.shoppingItem.is_bought || false
            })
            showSuccess('Shopping item updated successfully!')
        }

        emit('updated')
        closeModal()
    } catch (error) {
        showError(error, isNewItem.value ? 'create shopping item' : 'update shopping item')
    }
}

const attemptClose = () => {
    if (hasUnsavedChanges.value) {
        showUnsavedChangesDialog.value = true
    } else {
        closeModal()
    }
}

const closeModal = () => {
    resetForm()
    showUnsavedChangesDialog.value = false
    emit('close')
}

const keepEditing = () => {
    showUnsavedChangesDialog.value = false
}

const discardChanges = () => {
    closeModal()
}

// Watch for shopping item changes
watch(() => props.shoppingItem, (item) => {
    if (item) {
        resetForm()
    }
}, { immediate: true })

// Watch for modal visibility changes
watch(() => props.show, (show) => {
    if (show && props.shoppingItem) {
        resetForm()
    }
})
</script>